import "./App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "./lib/auth";
import Header from "./components/site/Header";
import Footer from "./components/site/Footer";

// Route-based code splitting. Each page becomes its own JS chunk so the
// initial bundle stays small (previously AdminDashboard alone bloated the
// main bundle by ~1000 LOC even for anonymous readers who never touch it).
// React.lazy + Suspense loads each chunk on demand; webpackChunkName magic
// comments produce human-readable filenames in build output for debugging.
const Home = lazy(() => import(/* webpackChunkName: "home" */ "./pages/Home"));
const Blog = lazy(() => import(/* webpackChunkName: "blog" */ "./pages/Blog"));
const PostDetail = lazy(() => import(/* webpackChunkName: "post-detail" */ "./pages/PostDetail"));
const CategoryPage = lazy(() => import(/* webpackChunkName: "category" */ "./pages/CategoryPage"));
const About = lazy(() => import(/* webpackChunkName: "about" */ "./pages/About"));
const Privacy = lazy(() => import(/* webpackChunkName: "privacy" */ "./pages/Privacy"));
const SearchPage = lazy(() => import(/* webpackChunkName: "search" */ "./pages/Search"));
const GroupPage = lazy(() => import(/* webpackChunkName: "group" */ "./pages/GroupPage"));
const AdminLogin = lazy(() => import(/* webpackChunkName: "admin-login" */ "./pages/AdminLogin"));
const AdminDashboard = lazy(() => import(/* webpackChunkName: "admin-dashboard" */ "./pages/AdminDashboard"));
const NotFound = lazy(() => import(/* webpackChunkName: "not-found" */ "./pages/NotFound"));

// Reset scroll to top whenever the route changes (unless we're navigating to a #hash).
// Disable the browser's automatic scroll restoration so back/forward navigation also
// lands the user at the top of the new page (rather than the footer of where they
// were previously) — gives consistent mobile UX.
function ScrollToTop() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if ("scrollRestoration" in window.history) {
            window.history.scrollRestoration = "manual";
        }
    }, []);
    useEffect(() => {
        if (hash) return; // let the browser scroll to the anchor
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, [pathname, hash]);
    return null;
}

// Send a GA4 page_view on every React-Router navigation. The bare gtag snippet
// in index.html only fires once on the first load (because we're a SPA — there
// are no full-page reloads when the user navigates). This hook keeps GA's
// pageview count accurate.
function GAPageview() {
    const { pathname, search } = useLocation();
    useEffect(() => {
        if (typeof window.gtag !== "function") return;
        window.gtag("event", "page_view", {
            page_path: pathname + search,
            page_location: window.location.href,
            page_title: document.title,
        });
    }, [pathname, search]);
    return null;
}

function PublicLayout({ children }) {
    return (
        <>
            <Header />
            <main>{children}</main>
            <Footer />
        </>
    );
}

// Suspense fallback shown while a lazy route chunk is downloading. Kept
// intentionally minimal (no spinner text/branding) so it's near-invisible
// on fast connections and doesn't create layout shift — most chunks are
// ~20-80KB gzip and land in <200ms on 4G. Height matches typical
// above-the-fold space so header/footer don't reflow when content arrives.
function RouteFallback() {
    return <div style={{ minHeight: "60vh" }} aria-hidden="true" />;
}

// Redirect /reviews/{slug} → /blog/{slug} so old shared links (or anyone who
// intuits "reviews" as the URL) don't hit blank pages. Uses `replace` so the
// history stack doesn't clutter with the intermediate URL.
function ReviewsSlugRedirect() {
    const { pathname } = useLocation();
    const slug = pathname.replace(/^\/reviews\/?/, "");
    return <Navigate to={`/blog/${slug}`} replace />;
}

export default function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <ScrollToTop />
                    <GAPageview />
                    <Toaster position="top-center" richColors />
                    <Suspense fallback={<RouteFallback />}>
                        <Routes>
                        <Route
                            path="/"
                            element={
                                <PublicLayout>
                                    <Home />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/blog"
                            element={
                                <PublicLayout>
                                    <Blog />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/blog/:slug"
                            element={
                                <PublicLayout>
                                    <PostDetail />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/category/:slug"
                            element={
                                <PublicLayout>
                                    <CategoryPage />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/dogs"
                            element={
                                <PublicLayout>
                                    <GroupPage />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/cats"
                            element={
                                <PublicLayout>
                                    <GroupPage />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/about"
                            element={
                                <PublicLayout>
                                    <About />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/privacy"
                            element={
                                <PublicLayout>
                                    <Privacy />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/search"
                            element={
                                <PublicLayout>
                                    <SearchPage />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/admin/login"
                            element={
                                <PublicLayout>
                                    <AdminLogin />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/admin"
                            element={
                                <PublicLayout>
                                    <AdminDashboard />
                                </PublicLayout>
                            }
                        />
                        {/* /reviews is the human-friendly URL people type and
                            what our nav label says, but the actual route is
                            /blog. Redirect keeps kiblco.com/reviews from
                            landing on a blank page. */}
                        <Route path="/reviews" element={<Navigate to="/blog" replace />} />
                        <Route path="/reviews/:slug" element={<ReviewsSlugRedirect />} />
                        {/* Catch-all 404. Renders a real "not found" page with
                            <meta name=robots content=noindex> so Googlebot
                            drops these URLs instead of stuffing them into the
                            "Page with redirect" bucket forever (which is what
                            happened when we used to blanket-redirect them). */}
                        <Route
                            path="*"
                            element={
                                <PublicLayout>
                                    <NotFound />
                                </PublicLayout>
                            }
                        />
                        </Routes>
                    </Suspense>
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}
